(() => {
  'use strict';

  const ELEMENT = 'lautio-capture-locked';
  const DESKTOP_MIN = 1100;
  const TIME_CONSTANT = 0.115;
  const MAX_VELOCITY = 2.4;
  const CHAPTERS = Object.freeze([0, 0.18, 0.36, 0.56, 0.76, 0.91, 1]);
  const SIZES = Object.freeze(['small', 'medium', 'large']);
  const KEYFRAMES = Object.freeze({
    large: [
      { p: 0, x: .22, y: .01, z: 0, s: .88, rx: 2, ry: -9, rz: 4, o: 1, shadow: .42 },
      { p: .15, x: .20, y: .02, z: 0, s: .82, rx: 5, ry: -18, rz: 1, o: 1, shadow: .38 },
      { p: .27, x: .27, y: .08, z: 0, s: .68, rx: 12, ry: -68, rz: -13, o: .2, shadow: .12 },
      { p: .34, x: .23, y: .05, z: 0, s: .72, rx: 9, ry: -58, rz: -10, o: .88, shadow: .2 },
      { p: .44, x: .22, y: 0, z: 0, s: 1.02, rx: 4, ry: -22, rz: 6, o: 1, shadow: .34 },
      { p: .56, x: .24, y: .08, z: -20, s: .64, rx: 8, ry: -14, rz: 7, o: 1, shadow: .27 },
      { p: .70, x: .24, y: .07, z: -20, s: .64, rx: 5, ry: -10, rz: 5, o: 1, shadow: .3 },
      { p: .77, x: .22, y: .02, z: 0, s: .76, rx: 4, ry: -16, rz: 3, o: 1, shadow: .38 },
      { p: .91, x: .13, y: .02, z: 0, s: .68, rx: 3, ry: -9, rz: 1, o: 1, shadow: .32 },
      { p: 1, x: .04, y: .02, z: 0, s: .58, rx: 2, ry: -6, rz: 0, o: .12, shadow: .1 },
    ],
    medium: [
      { p: 0, x: .04, y: .06, z: -60, s: .58, rx: 7, ry: -12, rz: 0, o: 0, shadow: .1 },
      { p: .50, x: .04, y: .08, z: -60, s: .58, rx: 7, ry: -12, rz: 0, o: 0, shadow: .1 },
      { p: .56, x: .04, y: .10, z: -15, s: .64, rx: 7, ry: -8, rz: 0, o: 1, shadow: .27 },
      { p: .70, x: .04, y: .09, z: -15, s: .64, rx: 5, ry: -6, rz: 0, o: 1, shadow: .3 },
      { p: .77, x: .04, y: .04, z: -40, s: .58, rx: 4, ry: -7, rz: 0, o: 0, shadow: .12 },
      { p: 1, x: .04, y: .04, z: -40, s: .58, rx: 4, ry: -7, rz: 0, o: 0, shadow: .12 },
    ],
    small: [
      { p: 0, x: -.16, y: .12, z: -90, s: .58, rx: 7, ry: -9, rz: -7, o: 0, shadow: .08 },
      { p: .50, x: -.16, y: .12, z: -90, s: .58, rx: 7, ry: -9, rz: -7, o: 0, shadow: .08 },
      { p: .56, x: -.16, y: .13, z: -30, s: .64, rx: 7, ry: -6, rz: -6, o: 1, shadow: .26 },
      { p: .70, x: -.16, y: .12, z: -30, s: .64, rx: 5, ry: -5, rz: -5, o: 1, shadow: .3 },
      { p: .77, x: -.16, y: .06, z: -55, s: .58, rx: 4, ry: -6, rz: -4, o: 0, shadow: .1 },
      { p: 1, x: -.16, y: .06, z: -55, s: .58, rx: 4, ry: -6, rz: -4, o: 0, shadow: .1 },
    ],
  });

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
  const fadeBand = (distance, width) => clamp(1 - Math.abs(distance) / width);

  function sampleFrames(frames, progress) {
    let start = frames[0];
    let end = frames.at(-1);
    for (let index = 0; index < frames.length - 1; index += 1) {
      if (progress >= frames[index].p && progress <= frames[index + 1].p) {
        start = frames[index]; end = frames[index + 1]; break;
      }
    }
    const amount = start === end ? 0 : smooth((progress - start.p) / Math.max(.0001, end.p - start.p));
    return Object.fromEntries(['x','y','z','s','rx','ry','rz','o','shadow'].map((key) => [key, lerp(start[key], end[key], amount)]));
  }

  class LautioCaptureLocked extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.track = this.querySelector('[data-v11-track]');
      this.chapters = [...this.querySelectorAll('[data-v11-chapter]')];
      this.environments = [...this.querySelectorAll('[data-v11-environment]')];
      this.boards = Object.fromEntries(SIZES.map((size) => [size, this.querySelector(`[data-v11-product-world] [data-board="${size}"]`)]));
      this.video = this.querySelector('[data-v11-dishwasher-video]');
      this.contact = this.querySelector('[data-v11-rack-contact]');
      this.progressMeter = this.querySelector('[data-v11-progress]');
      this.sticky = this.querySelector('[data-v11-sticky]');
      this.products = this.readProducts();
      this.selected = this.readInitialSize();
      this.selectorMix = { small: 0, medium: 0, large: 1 };
      this.selectorTarget = { ...this.selectorMix };
      this.targetProgress = 0;
      this.renderedProgress = 0;
      this.lastTime = 0;
      this.lastVideoSeekAt = 0;
      this.raf = 0;
      this.handleMotion = this.handleMotion.bind(this);
      this.frame = this.frame.bind(this);
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = window.matchMedia(`(min-width:${DESKTOP_MIN}px) and (hover:hover) and (pointer:fine)`);
      this.setupSelectors();
      this.setupCommerce();
      this.configureMode();
      this.reduced.addEventListener('change', this.handleMotion);
      this.desktop.addEventListener('change', this.handleMotion);
      document.addEventListener('shopify:section:load', this.handleMotion, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.abort?.abort();
      this.modeAbort?.abort();
      this.reduced?.removeEventListener('change', this.handleMotion);
      this.desktop?.removeEventListener('change', this.handleMotion);
      this.initialized = false;
    }

    readProducts() {
      try { return JSON.parse(this.querySelector('[data-lautio-products]')?.textContent || '{}'); }
      catch (_error) { return {}; }
    }

    readInitialSize() {
      try { const size = new URL(location.href).searchParams.get('size'); if (SIZES.includes(size)) return size; }
      catch (_error) { /* use markup default */ }
      return this.dataset.selectedSize || 'large';
    }

    handleMotion() { this.configureMode(); }

    configureMode() {
      this.modeAbort?.abort();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.modeAbort = new AbortController();
      const full = this.dataset.motionEnabled === 'true' && this.desktop.matches && !this.reduced.matches;
      this.dataset.mode = full ? 'full' : 'lite';
      if (full) {
        this.hydrateDesktopAssets();
        addEventListener('scroll', () => this.requestFrame(), { passive: true, signal: this.modeAbort.signal });
        addEventListener('resize', () => this.requestFrame(), { passive: true, signal: this.modeAbort.signal });
        addEventListener('orientationchange', () => this.requestFrame(), { passive: true, signal: this.modeAbort.signal });
        this.targetProgress = this.measureProgress();
        this.renderedProgress = this.targetProgress;
        this.render(this.renderedProgress);
        this.requestFrame();
      } else {
        this.updateSelectionUI();
      }
    }

    hydrateDesktopAssets() {
      const desktop = this.querySelector('[data-v11-desktop]');
      if (!desktop || desktop.dataset.assetsReady === 'true') return;
      desktop.querySelectorAll('[data-srcset]').forEach((node) => {
        node.srcset = node.dataset.srcset;
        node.removeAttribute('data-srcset');
      });
      desktop.querySelectorAll('img[data-src]').forEach((image) => {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      });
      const video = desktop.querySelector('[data-v11-dishwasher-video]');
      if (video) {
        if (video.dataset.poster) video.poster = video.dataset.poster;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
      }
      desktop.dataset.assetsReady = 'true';
    }

    measureProgress() {
      if (!this.track) return 0;
      const rect = this.track.getBoundingClientRect();
      const travel = Math.max(1, rect.height - innerHeight);
      return clamp(-rect.top / travel);
    }

    requestFrame() {
      if (this.dataset.mode === 'full') this.targetProgress = this.measureProgress();
      if (!this.raf) this.raf = requestAnimationFrame(this.frame);
    }

    frame(now) {
      this.raf = 0;
      const dt = Math.min(.05, Math.max(.001, this.lastTime ? (now - this.lastTime) / 1000 : .016));
      this.lastTime = now;
      const alpha = 1 - Math.exp(-dt / TIME_CONSTANT);
      const delta = this.targetProgress - this.renderedProgress;
      const step = Math.sign(delta) * Math.min(Math.abs(delta) * alpha, MAX_VELOCITY * dt);
      this.renderedProgress = clamp(this.renderedProgress + step);
      let selectionMoving = false;
      SIZES.forEach((size) => {
        const next = lerp(this.selectorMix[size], this.selectorTarget[size], 1 - Math.exp(-dt / .19));
        selectionMoving ||= Math.abs(next - this.selectorTarget[size]) > .002;
        this.selectorMix[size] = next;
      });
      if (this.dataset.mode === 'full') this.render(this.renderedProgress);
      if (Math.abs(delta) > .00025 || selectionMoving) this.requestFrame();
    }

    render(progress) {
      this.style.setProperty('--story-progress', progress.toFixed(5));
      this.progressMeter?.style.setProperty('--progress', progress.toFixed(4));
      this.dataset.storyActive = String(progress > .015 && progress < .985);
      this.dataset.afterStory = String((this.track?.getBoundingClientRect().bottom || innerHeight + 1) < innerHeight * .72);
      const phase = Math.min(CHAPTERS.length - 2, CHAPTERS.findIndex((point, index) => index < CHAPTERS.length - 1 && progress <= CHAPTERS[index + 1]));
      const local = clamp((progress - CHAPTERS[phase]) / (CHAPTERS[phase + 1] - CHAPTERS[phase]));

      this.chapters.forEach((chapter, index) => {
        const enter = phase === 0 ? 1 : smooth(clamp(local / .13));
        const exit = phase === this.chapters.length - 1 ? 1 : smooth(clamp((1 - local) / .10));
        const opacity = index === phase ? Math.min(enter, exit) : 0;
        chapter.style.setProperty('--copy-opacity', opacity.toFixed(3));
        chapter.style.setProperty('--copy-y', `${lerp(22, -10, local).toFixed(1)}px`);
        chapter.classList.toggle('is-active', opacity > .55);
        chapter.setAttribute('aria-hidden', opacity > .18 ? 'false' : 'true');
      });

      this.environments.forEach((environment, index) => {
        const center = (CHAPTERS[index] + CHAPTERS[index + 1]) / 2;
        const width = (CHAPTERS[index + 1] - CHAPTERS[index]) * .8;
        const opacity = Math.max(index === phase ? 1 : 0, fadeBand(progress - center, width));
        environment.style.setProperty('--env-opacity', clamp(opacity).toFixed(3));
        environment.style.setProperty('--env-scale', lerp(1.045, 1, local).toFixed(4));
      });

      SIZES.forEach((size) => this.renderBoard(size, sampleFrames(KEYFRAMES[size], progress), progress));
      const ch1Local = clamp((progress - CHAPTERS[1]) / (CHAPTERS[2] - CHAPTERS[1]));
      const videoIntro = smooth(clamp((progress - CHAPTERS[1]) / .045));
      const videoOutro = 1 - smooth(clamp((progress - CHAPTERS[2]) / .04));
      const videoOpacity = progress >= CHAPTERS[1] && progress <= CHAPTERS[2] + .04 ? videoIntro * videoOutro : 0;
      if (this.video) {
        this.video.style.setProperty('--video-opacity', videoOpacity.toFixed(3));
        this.video.style.setProperty('--video-scale', lerp(.97, 1, ch1Local).toFixed(4));
        if (videoOpacity > .05 && this.video.readyState >= 1) {
          const desired = lerp(.2, Math.max(.2, this.video.duration - .12), smooth(ch1Local));
          const now = performance.now();
          if (Math.abs(this.video.currentTime - desired) > .08 && !this.video.seeking && now - this.lastVideoSeekAt > 90) {
            this.video.currentTime = desired;
            this.lastVideoSeekAt = now;
          }
        }
      }
      this.contact?.style.setProperty('--contact-opacity', (videoOpacity * smooth((ch1Local - .62) / .3)).toFixed(3));
      this.sticky?.classList.toggle('is-visible', progress > .30 || scrollY > (this.track?.offsetHeight || 0));
    }

    renderBoard(size, state, progress) {
      const board = this.boards[size];
      if (!board) return;
      if (size === 'large' && progress >= CHAPTERS[1] && progress < CHAPTERS[2] + .04) {
        const physicalTakeover = smooth(clamp((progress - CHAPTERS[1]) / .045));
        state.o *= 1 - physicalTakeover;
      } else if (size === 'large' && progress >= CHAPTERS[2] + .04 && progress < CHAPTERS[2] + .08) {
        state.o *= smooth((progress - CHAPTERS[2] - .04) / .04);
      }
      const familyVisible = progress >= .50 && progress <= .77;
      const selectedInfluence = smooth(clamp((progress - .55) / .08)) * smooth(clamp((.78 - progress) / .08));
      const mix = this.selectorMix[size];
      const index = SIZES.indexOf(size);
      const selectedIndex = SIZES.indexOf(this.selected);
      if (familyVisible) {
        state.z += selectedInfluence * (mix * 110 - (1 - mix) * 30);
        state.s *= 1 + selectedInfluence * (mix * .09 - (1 - mix) * .035);
        state.y -= selectedInfluence * mix * .018;
        state.x += selectedInfluence * (index - selectedIndex) * .012 * (1 - mix);
      } else if (progress > .77 && size !== this.selected) {
        state.o = 0;
      } else if (progress > .77 && size === this.selected && size !== 'large') {
        const leader = sampleFrames(KEYFRAMES.large, progress);
        state = { ...leader, o: leader.o };
      }
      board.style.setProperty('--x', `${state.x * innerWidth}px`);
      board.style.setProperty('--y', `${state.y * innerHeight}px`);
      board.style.setProperty('--z', `${state.z}px`);
      board.style.setProperty('--scale', state.s.toFixed(4));
      board.style.setProperty('--rx', `${state.rx.toFixed(2)}deg`);
      board.style.setProperty('--ry', `${state.ry.toFixed(2)}deg`);
      board.style.setProperty('--rz', `${state.rz.toFixed(2)}deg`);
      board.style.setProperty('--opacity', clamp(state.o).toFixed(3));
      board.style.setProperty('--shadow-opacity', state.shadow.toFixed(3));
      board.style.setProperty('--board-brightness', (familyVisible ? lerp(.89, 1.04, mix) : 1).toFixed(3));
      board.style.zIndex = String(Math.round(100 + state.z));
      board.setAttribute('aria-hidden', state.o > .1 ? 'false' : 'true');
    }

    setupSelectors() {
      // The whole card is the radio now, and the card contains a 3D board the
      // reader drags to turn. A drag ends in a click, so without this a turn
      // of the Small board silently changed the chosen size.
      let downX = 0, downY = 0;
      this.addEventListener('pointerdown', (event) => { downX = event.clientX; downY = event.clientY; }, { signal: this.abort.signal, capture: true });
      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-v11-size]');
        if (!control) return;
        const moved = Math.hypot(event.clientX - downX, event.clientY - downY);
        if (moved > 8) return;   // that was a drag, not a choice
        this.selectSize(control.dataset.v11Size);
      }, { signal: this.abort.signal });
      this.addEventListener('keydown', (event) => {
        const control = event.target.closest('[role="radio"][data-v11-size]');
        if (!control) return;
        // a card is a radio, so space and enter choose it and the arrows move
        // between them - the shop cards are focusable elements now, not buttons
        if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
          event.preventDefault();
          this.selectSize(control.dataset.v11Size);
          return;
        }
        if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const direction = ['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1;
        const next = SIZES[(SIZES.indexOf(control.dataset.v11Size) + direction + SIZES.length) % SIZES.length];
        this.selectSize(next);
        this.querySelector(`[role="radio"][data-v11-size="${next}"]`)?.focus();
      }, { signal: this.abort.signal });
      this.selectSize(this.selected, false);
    }

    selectSize(size, animate = true) {
      if (!SIZES.includes(size)) return;
      this.selected = size;
      this.dataset.selectedSize = size;
      SIZES.forEach((key) => { this.selectorTarget[key] = key === size ? 1 : 0; });
      this.updateSelectionUI();
      try {
        const url = new URL(location.href); url.searchParams.set('size', size); history.replaceState({}, '', url);
      } catch (_error) { /* URL state is optional */ }
      if (animate) this.requestFrame();
    }

    updateSelectionUI() {
      const product = this.products[this.selected] || {};
      this.querySelectorAll('[data-v11-size]').forEach((control) => {
        const active = control.dataset.v11Size === this.selected;
        if (control.getAttribute('role') === 'radio') {
          control.setAttribute('aria-checked', String(active));
          // one stop per group: tab reaches the chosen card, the arrows do the rest
          if (control.hasAttribute('tabindex')) control.setAttribute('tabindex', active ? '0' : '-1');
        }
        if (control.hasAttribute('aria-pressed')) control.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-v11-product-card]').forEach((card) => card.classList.toggle('is-selected', card.dataset.v11ProductCard === this.selected));
      const set = (selector, value) => { const node = this.querySelector(selector); if (node) node.textContent = value || ''; };
      set('[data-v11-detail-name]', product.label || this.selected);
      set('[data-v11-detail-dimensions]', product.dimensions);
      set('[data-v11-detail-use]', product.use);
      set('[data-v11-sticky-label]', `${product.label || this.selected} · ${product.dimensions || ''}`);
      this.querySelectorAll('[data-v11-buy]').forEach((button) => this.configureBuyButton(button, this.products[button.dataset.v11Buy] || {}));
      const detailBuy = this.querySelector('[data-v11-detail-buy]');
      if (detailBuy) { detailBuy.dataset.v11Buy = this.selected; this.configureBuyButton(detailBuy, product); }
    }

    configureBuyButton(button, product) {
      const ready = Boolean(product.released && product.available && product.variantId);
      button.disabled = !ready;
      // before launch the editor's own labels stay; they are only replaced by
      // a real action and a real price once a product is published
      if (ready) button.textContent = `Add ${product.label} to cart`;
      const card = button.closest('[data-v11-product-card]');
      const price = card?.querySelector('[data-v11-card-price]');
      if (price && product.released && product.price) price.textContent = product.price;
    }

    setupCommerce() {
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-v11-buy]');
        if (!button || button.disabled) return;
        const product = this.products[button.dataset.v11Buy];
        if (!product?.variantId) return;
        button.disabled = true;
        button.textContent = 'Adding…';
        try {
          const sections = 'cart-drawer,cart-icon-bubble';
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }], sections, sections_url: location.pathname }),
          });
          if (!response.ok) throw new Error(`Cart ${response.status}`);
          const payload = await response.json();
          const drawer = document.querySelector('cart-drawer');
          if (drawer?.renderContents) drawer.renderContents(payload);
          document.dispatchEvent(new CustomEvent('lautio:cart-added', { detail: { size: this.selected, payload } }));
        } catch (error) {
          button.textContent = 'Try again';
          console.error('[Lautio V1.1] add to cart failed', error);
        } finally {
          setTimeout(() => this.updateSelectionUI(), 1200);
        }
      }, { signal: this.abort.signal });
    }

    setReviewProgress(value) {
      const progress = clamp(Number(value) || 0);
      this.targetProgress = progress;
      this.renderedProgress = progress;
      if (this.dataset.mode === 'full') this.render(progress);
      return this.reviewState();
    }

    setReviewSelectionTransition(from, to, value) {
      if (!SIZES.includes(from) || !SIZES.includes(to)) return this.reviewState();
      const amount = smooth(clamp(Number(value) || 0));
      this.selected = to;
      this.dataset.selectedSize = to;
      SIZES.forEach((size) => {
        this.selectorMix[size] = size === from ? 1 - amount : size === to ? amount : 0;
        this.selectorTarget[size] = size === to ? 1 : 0;
      });
      this.updateSelectionUI();
      if (this.dataset.mode === 'full') this.render(this.renderedProgress);
      return this.reviewState();
    }

    reviewState() {
      return {
        progress: this.renderedProgress,
        target: this.targetProgress,
        selected: this.selected,
        mode: this.dataset.mode,
      };
    }
  }

  if (!customElements.get(ELEMENT)) customElements.define(ELEMENT, LautioCaptureLocked);
})();
